import React from 'react';
import { Link } from 'react-router-dom';
import { Info, ArrowLeft, Zap, Shield, Code, Heart } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * 关于页面
 */
function About() {
  const { language } = useLanguage();

  const content = {
    zh: {
      title: '关于 DevToolkit',
      subtitle: '为开发者打造的免费在线工具集',
      sections: [
        {
          icon: Heart,
          title: '我们的使命',
          content: `
            <p>DevToolkit 致力于为开发者提供<strong>简单、快速、免费</strong>的在线开发工具。</p>
            <p>我们相信好的工具应该：</p>
            <ul>
              <li>✅ 无需安装，打开浏览器即用</li>
              <li>✅ 完全免费，没有功能限制</li>
              <li>✅ 注重隐私，数据本地处理</li>
              <li>✅ 用户体验优先，界面简洁美观</li>
            </ul>
          `
        },
        {
          icon: Zap,
          title: '我们的优势',
          content: `
            <h3>1. Postman 风格的表格视图</h3>
            <p>我们的 JSON 工具采用类似 Postman 的表格视图，让复杂的 JSON 数据一目了然。无需安装 Postman，在线即可享受同样强大的 JSON 查看体验。</p>

            <h3>2. 智能错误修复</h3>
            <p>自动检测并修复常见的 JSON 格式错误（尾随逗号、单引号、缺少引号等），一键搞定格式问题。</p>

            <h3>3. 多标签页对比</h3>
            <p>支持同时打开多个 JSON 文件，方便对比不同 API 返回的数据结构或查看数据变化。</p>

            <h3>4. 内层 JSON 解析</h3>
            <p>当 JSON 值本身是字符串形式的 JSON 时，可以智能识别并进一步解析，适合处理 API 返回的嵌套 JSON。</p>

            <h3>5. 隐私保护</h3>
            <p>所有工具数据（JSON、时间戳等）<strong>完全在浏览器本地处理</strong>，不会上传到服务器，保护您的数据隐私。</p>
          `
        },
        {
          icon: Code,
          title: '可用工具',
          content: `
            <h3>JSON 格式化工具</h3>
            <ul>
              <li>JSON 格式化和美化</li>
              <li>Postman 风格表格视图</li>
              <li>树状视图展示结构</li>
              <li>智能错误检测和修复</li>
              <li>多标签页对比</li>
              <li>内层 JSON 解析</li>
            </ul>

            <h3>时间戳转换工具</h3>
            <ul>
              <li>Unix 时间戳与日期互转</li>
              <li>支持秒级和毫秒级</li>
              <li>实时显示当前时间</li>
              <li>多种日期格式支持</li>
            </ul>

            <h3>Go Struct ↔ JSON 转换</h3>
            <ul>
              <li>JSON 转 Go 结构体</li>
              <li>Go 结构体转 JSON</li>
              <li>自动生成 tag 标签</li>
              <li>支持嵌套结构</li>
            </ul>
          `
        },
        {
          icon: Shield,
          title: '技术特点',
          content: `
            <ul>
              <li><strong>React 18：</strong>使用最新的 React 技术栈</li>
              <li><strong>Tailwind CSS：</strong>现代化的 UI 设计</li>
              <li><strong>本地处理：</strong>数据完全在浏览器处理，不上传服务器</li>
              <li><strong>暗黑模式：</strong>支持亮色/暗色主题切换</li>
              <li><strong>响应式设计：</strong>完美适配手机、平板、电脑</li>
              <li><strong>多语言支持：</strong>中文、英文界面切换</li>
              <li><strong>PWA 支持：</strong>可添加到主屏幕，离线使用</li>
            </ul>
          `
        }
      ],
      team: {
        title: '关于团队',
        content: 'DevToolkit 由热爱开发的工程师创建和维护。我们相信好的工具能提升开发效率，让编程更快乐。'
      },
      opensource: {
        title: '开源',
        content: 'DevToolkit 是开源项目，欢迎在 <a href="https://github.com/RongShiMr/JsonPreview" target="_blank" rel="noopener noreferrer">GitHub</a> 上查看源代码、提交问题或贡献代码。'
      },
      contact: {
        title: '联系我们',
        content: '如果您有任何建议或问题，欢迎通过 <a href="/contact">联系页面</a> 或 <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a> 与我们联系。'
      }
    },
    en: {
      title: 'About DevToolkit',
      subtitle: 'Free Online Tools for Developers',
      sections: [
        {
          icon: Heart,
          title: 'Our Mission',
          content: `
            <p>DevToolkit is dedicated to providing <strong>simple, fast, and free</strong> online development tools for developers.</p>
            <p>We believe good tools should:</p>
            <ul>
              <li>✅ Work without installation, just open your browser</li>
              <li>✅ Be completely free with no feature limits</li>
              <li>✅ Respect privacy with local data processing</li>
              <li>✅ Prioritize user experience with clean, beautiful interfaces</li>
            </ul>
          `
        },
        {
          icon: Zap,
          title: 'Our Advantages',
          content: `
            <h3>1. Postman-style Table View</h3>
            <p>Our JSON tool features a Postman-style table view that makes complex JSON data easy to understand. No Postman installation needed - enjoy the same powerful JSON viewing experience online.</p>

            <h3>2. Smart Error Fixing</h3>
            <p>Automatically detect and fix common JSON format errors (trailing commas, single quotes, missing quotes, etc.) with one click.</p>

            <h3>3. Multi-Tab Comparison</h3>
            <p>Open multiple JSON files simultaneously to compare data structures from different APIs or view data changes.</p>

            <h3>4. Inner JSON Parsing</h3>
            <p>When a JSON value is itself a stringified JSON, it can be intelligently recognized and further parsed - perfect for handling nested JSON from API responses.</p>

            <h3>5. Privacy Protection</h3>
            <p>All tool data (JSON, timestamps, etc.) is <strong>processed completely locally in your browser</strong> and never uploaded to servers, protecting your data privacy.</p>
          `
        },
        {
          icon: Code,
          title: 'Available Tools',
          content: `
            <h3>JSON Formatter</h3>
            <ul>
              <li>JSON formatting and beautification</li>
              <li>Postman-style table view</li>
              <li>Tree view for structure display</li>
              <li>Smart error detection and fixing</li>
              <li>Multi-tab comparison</li>
              <li>Inner JSON parsing</li>
            </ul>

            <h3>Timestamp Converter</h3>
            <ul>
              <li>Unix timestamp to datetime conversion</li>
              <li>Support for seconds and milliseconds</li>
              <li>Real-time current timestamp display</li>
              <li>Multiple date format support</li>
            </ul>

            <h3>Go Struct ↔ JSON Converter</h3>
            <ul>
              <li>JSON to Go struct</li>
              <li>Go struct to JSON</li>
              <li>Auto-generate tag annotations</li>
              <li>Support for nested structures</li>
            </ul>
          `
        },
        {
          icon: Shield,
          title: 'Technical Features',
          content: `
            <ul>
              <li><strong>React 18:</strong> Built with the latest React technology</li>
              <li><strong>Tailwind CSS:</strong> Modern UI design</li>
              <li><strong>Local Processing:</strong> All data processed in browser, no server upload</li>
              <li><strong>Dark Mode:</strong> Light/dark theme switching</li>
              <li><strong>Responsive Design:</strong> Perfect on mobile, tablet, and desktop</li>
              <li><strong>Multi-language:</strong> Chinese and English interface</li>
              <li><strong>PWA Support:</strong> Add to home screen, offline usage</li>
            </ul>
          `
        }
      ],
      team: {
        title: 'About the Team',
        content: 'DevToolkit is created and maintained by engineers who love development. We believe good tools improve development efficiency and make programming more enjoyable.'
      },
      opensource: {
        title: 'Open Source',
        content: 'DevToolkit is an open-source project. Feel free to view the source code, submit issues, or contribute on <a href="https://github.com/RongShiMr/JsonPreview" target="_blank" rel="noopener noreferrer">GitHub</a>.'
      },
      contact: {
        title: 'Contact Us',
        content: 'If you have any suggestions or questions, please contact us through the <a href="/contact">Contact Page</a> or <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>.'
      }
    }
  };

  const data = content[language];

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* 页面标题 */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Info size={28} className="text-indigo-600 dark:text-indigo-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{data.title}</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">{data.subtitle}</p>
        </div>
        {/* Main Sections */}
        <div className="space-y-12">
          {data.sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <section key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                    <Icon size={24} className="text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{section.title}</h2>
                </div>
                <div
                  className="prose prose-lg dark:prose-invert max-w-none
                    prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
                    prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                    prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
                    prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
                    prose-ul:my-4 prose-li:my-1
                  "
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </section>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-12 space-y-8">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-8 border border-indigo-200 dark:border-indigo-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{data.team.title}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">{data.team.content}</p>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-8 border border-purple-200 dark:border-purple-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{data.opensource.title}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed prose dark:prose-invert prose-a:text-indigo-600 dark:prose-a:text-indigo-400" dangerouslySetInnerHTML={{ __html: data.opensource.content }} />
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{data.contact.title}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed prose dark:prose-invert prose-a:text-indigo-600 dark:prose-a:text-indigo-400" dangerouslySetInnerHTML={{ __html: data.contact.content }} />
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </article>
    </div>
  );
}

export default About;
